import { useState } from 'react'

import { createEmptyDriver } from '@/components/DriversTable'
import { Button } from '@/components/ui/button'
import { type Driver, formatDriverDates } from '@/models/driver'
import { type PolicyChangeInput, PolicyChangeKind } from '@/models/policyChange'
import { DriverModal } from './DriverModal'
import { toIsoDate } from './policyChangeCopy'

interface AddDriverFormProps {
  /** The roster's drivers, so the form catches a license or email already on it. */
  existing: Driver[]
  /** Rejects with the API's error, which the form shows on its fields. */
  onAdd: (change: PolicyChangeInput) => Promise<void>
}

/** A driver to add, on the Drivers page's own form, plus their first scheduled drive. */
export function AddDriverForm({ existing, onAdd }: AddDriverFormProps) {
  const [driver, setDriver] = useState<Driver | null>(null)

  async function add(added: Driver, firstDrive: Date | undefined) {
    await onAdd({
      kind: PolicyChangeKind.ADD_DRIVER,
      unit: {
        ...formatDriverDates({
          firstName: added.firstName,
          lastName: added.lastName,
          email: added.email,
          mobile: added.mobile,
          dob: added.dob,
          licenseState: added.licenseState,
          licenseNumber: added.licenseNumber,
          dateOfHire: added.dateOfHire,
          cdlDate: added.cdlDate,
          violationsCount: added.violationsCount,
          accidentsCount: added.accidentsCount,
          suspensionsCount: added.suspensionsCount,
        }),
      },
      startDate: toIsoDate(firstDrive),
    })
    setDriver(null)
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="self-start"
        onClick={() => setDriver(createEmptyDriver())}
      >
        Add driver
      </Button>
      {driver && (
        <DriverModal
          driver={driver}
          isNew
          existing={existing}
          onClose={() => setDriver(null)}
          onSubmit={add}
        />
      )}
    </>
  )
}
